import {
	DefaultExtensionProvider,
	type ExtensionAutoConvertHandler,
	type ExtensionManifest,
	type ExtensionProvider,
} from '@atlaskit/editor-common/extensions';
import {
	P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE,
	EXTENSION_NAMESPACE,
} from '../utils/constants';
import { type ForgeExtensionManifest, type ForgeExtensionParameters } from '../types';
import { AnalyticsAction, type AnalyticsClient } from '../utils/analytics';
import { fg } from '@atlaskit/platform-feature-flags';

/**
 * When connect module is migrated to forge, look up for forge extension that matches connect macro key.
 * This is to allow migrated connect macro data to be rendered with forge extension.
 */

type ManifestType = ExtensionManifest<ForgeExtensionParameters> | ForgeExtensionManifest;

export type SendAnalyticsFunc = (extensionKey: string, errorMessage: string) => unknown;

export class ForgeExtensionProvider extends DefaultExtensionProvider<ForgeExtensionParameters> {
	connectExtensionProvider: Promise<ExtensionProvider> | undefined;
	sendAnalytics: SendAnalyticsFunc | undefined;
	analyticsClient: AnalyticsClient | undefined;
	isMigratedFromAliasEnabled: boolean;

	constructor(
		manifests: ManifestType[] | Promise<ManifestType[]>,
		connectExtensionProvider?: Promise<ExtensionProvider>,
		sendAnalytics?: SendAnalyticsFunc,
		autoConvertHandlers?: ExtensionAutoConvertHandler[],
		analyticsClient?: AnalyticsClient,
		isMigratedFromAliasEnabled = false,
	) {
		super(manifests);
		this.connectExtensionProvider = connectExtensionProvider;
		this.sendAnalytics = sendAnalytics;
		this.manifestsPromise = Promise.resolve(manifests);
		this.autoConvertHandlers = autoConvertHandlers;
		this.analyticsClient = analyticsClient;
		this.isMigratedFromAliasEnabled = isMigratedFromAliasEnabled;
	}

	async getExtension(type: string, key: string): Promise<ManifestType> {
		try {
			return await super.getExtension(type, key);
		} catch (forgeError) {
			// There is no Forge app to match the full ARI extensionKey
			// If the extensionType is Connect, we fall back to trying to resolve it by harmonised connect module key
			if (type === P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE) {
				const { sendEvent } = this.analyticsClient || {};
				if (!this.connectExtensionProvider) {
					if (sendEvent) {
						sendEvent(AnalyticsAction.H11N_RESOLVE_FAILED, {
							extensionKey: key,
							error: 'No connect extension provider',
						});
					} else {
						this.sendAnalytics?.(key, 'No connect extension provider');
					}
				} else {
					let forgeExtensionManifests: ForgeExtensionManifest[] | undefined;
					if (this.isMigratedFromAliasEnabled) {
						forgeExtensionManifests = this.getForgeExtensionManifests(await this.getExtensions());
						const migratedFromExtension = this.getMigratedFromConnectToForgeExtension(
							key,
							forgeExtensionManifests,
						);
						if (migratedFromExtension) {
							return migratedFromExtension;
						}
					}

					try {
						// We first check that no Connect app still exists that can handle this extensionKey
						await (await this.connectExtensionProvider).getExtension(type, key);
					} catch {
						// We know that there is no corresponding Connect app, so we can look up the Forge app by Connect key

						const extension = await this.getConnectToForgeExtension(
							key,
							forgeExtensionManifests,
							false,
						);
						if (extension) {
							return extension;
						}

						if (fg('platform_upgrade_p2_to_forge_macro')) {
							const getP2ToForgeExtension = await this.getP2ToForgeExtension(key);

							if (getP2ToForgeExtension) {
								if (sendEvent) {
									sendEvent(AnalyticsAction.APP_MIGRATION_P2_MACRO_RESOLVED, {
										extensionKey: key,
										extensionType: P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE,
									});
								}
								return getP2ToForgeExtension;
							}
						}

						// Report failure to find a Forge extension for this Connect key
						if (sendEvent) {
							sendEvent(AnalyticsAction.H11N_RESOLVE_FAILED, {
								extensionKey: key,
								error: forgeError,
							});
						} else {
							this.sendAnalytics?.(
								key,
								forgeError instanceof Error ? forgeError.message : String(forgeError),
							);
						}
					}
				}
			}
			throw forgeError;
		}
	}

	async getExtensions(): Promise<ManifestType[]> {
		return super.getExtensions();
	}

	getPreloadedExtension(type: string, key: string): ManifestType | undefined {
		try {
			return super.getPreloadedExtension(type, key) as ManifestType;
		} catch (forgeError) {
			if (this.isMigratedFromAliasEnabled) {
				const migratedFromExtension = this.getPreloadedMigratedFromExtension(type, key);
				if (migratedFromExtension) {
					return migratedFromExtension;
				}
			}

			throw forgeError;
		}
	}

	async getMigratedFromExtension(
		type: string,
		key: string,
	): Promise<ForgeExtensionManifest | undefined> {
		if (
			!this.isMigratedFromAliasEnabled ||
			type !== P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE
		) {
			return undefined;
		}

		return this.getMigratedFromConnectToForgeExtension(
			key,
			this.getForgeExtensionManifests(await this.getExtensions()),
		);
	}

	getPreloadedMigratedFromExtension(type: string, key: string): ForgeExtensionManifest | undefined {
		if (
			!this.isMigratedFromAliasEnabled ||
			type !== P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE
		) {
			return undefined;
		}

		return this.getMigratedFromConnectToForgeExtension(
			key,
			this.getForgeExtensionManifests(this.manifestsCache),
		);
	}

	async getP2ToForgeExtension(key: string): Promise<ForgeExtensionManifest | undefined> {
		try {
			return (await super.getExtension(EXTENSION_NAMESPACE, key)) as ForgeExtensionManifest;
		} catch (_) {
			// Means no extension was found
			return;
		}
	}

	async getConnectToForgeExtension(
		key: string,
		forgeExtensionManifests?: ForgeExtensionManifest[],
		includeMigratedFromAliases = true,
	): Promise<ForgeExtensionManifest | undefined> {
		const manifests =
			forgeExtensionManifests ?? this.getForgeExtensionManifests(await this.getExtensions());

		for (const manifest of manifests) {
			const hasMigrationKeyMatch =
				Boolean(manifest.migrationKey) && manifest.connectModuleKey === key;

			if (hasMigrationKeyMatch) {
				return manifest;
			}
		}

		return includeMigratedFromAliases && this.isMigratedFromAliasEnabled
			? this.findMigratedFromMatch(key, manifests)
			: undefined;
	}

	private getMigratedFromConnectToForgeExtension(
		key: string,
		forgeExtensionManifests: ForgeExtensionManifest[],
	): ForgeExtensionManifest | undefined {
		return this.findMigratedFromMatch(key, forgeExtensionManifests);
	}

	private getForgeExtensionManifests(extensions: ManifestType[]): ForgeExtensionManifest[] {
		return extensions.filter(
			(manifest: ManifestType): manifest is ForgeExtensionManifest =>
				'connectModuleKey' in manifest,
		);
	}

	private findMigratedFromMatch(
		key: string,
		forgeExtensionManifests: ForgeExtensionManifest[],
	): ForgeExtensionManifest | undefined {
		return forgeExtensionManifests.find((manifest) =>
			Boolean(manifest.migratedFrom?.includes(key)),
		);
	}
}
